import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Hook for managing client and matter filter state.
 * Persists filter state to localStorage scoped by user email — mirrors
 * useApplicationFilter so the same browser, used by different users, keeps
 * each user's filter selection independent.
 */

interface UseClientMatterFilterReturn {
  /** Selected client name (null = no client filter) */
  clientFilter: string | null;

  /** Selected matter id (null = no matter filter) */
  matterFilter: string | null;

  /** Set the client filter (also clears matter, since matter is scoped to client) */
  setClientFilter: (client: string | null) => void;

  /** Set the matter filter; pass an optional client to auto-fill it (e.g., selecting a matter pre-fills its parent client) */
  setMatterFilter: (matterId: string | null, client?: string | null) => void;

  /** Clear both filters */
  clearAll: () => void;

  /** Filter an entries array by the current client/matter selection */
  filterByClientMatter: <T extends { matter_id?: string; client?: string }>(entries: T[]) => T[];

  /** Whether any filter is currently active */
  isFilterActive: boolean;
}

interface StoredFilter {
  client: string | null;
  matter: string | null;
}

const STORAGE_KEY_PREFIX = "hourglass_client_matter_filter_";

function getStorageKey(userEmail: string): string {
  const sanitized = userEmail.replace(/[^a-zA-Z0-9@._-]/g, "_");
  return `${STORAGE_KEY_PREFIX}${sanitized}`;
}

function loadFromStorage(userEmail: string): StoredFilter {
  if (!userEmail) return { client: null, matter: null };
  try {
    const stored = localStorage.getItem(getStorageKey(userEmail));
    if (!stored) return { client: null, matter: null };
    const parsed = JSON.parse(stored);
    return {
      client: typeof parsed.client === "string" ? parsed.client : null,
      matter: typeof parsed.matter === "string" ? parsed.matter : null,
    };
  } catch {
    return { client: null, matter: null };
  }
}

function saveToStorage(userEmail: string, value: StoredFilter): void {
  if (!userEmail) return;
  try {
    localStorage.setItem(getStorageKey(userEmail), JSON.stringify(value));
  } catch {
    // Storage quota exceeded or other error — fail silently
  }
}

export function useClientMatterFilter(userEmail: string): UseClientMatterFilterReturn {
  const initial = loadFromStorage(userEmail);
  const [clientFilter, setClientFilterState] = useState<string | null>(initial.client);
  const [matterFilter, setMatterFilterState] = useState<string | null>(initial.matter);

  // Skip the persist effect that fires from the load effect's setState. Without
  // this, when userEmail flips from "" to a real email on auth, the persist
  // effect runs with stale null/null state in the same flush and nulls out the
  // stored filter before the load effect's state update is applied.
  const skipNextPersist = useRef(true);

  // Reload from storage when userEmail changes (different user logged in)
  useEffect(() => {
    const loaded = loadFromStorage(userEmail);
    setClientFilterState(loaded.client);
    setMatterFilterState(loaded.matter);
    skipNextPersist.current = true;
  }, [userEmail]);

  // Persist to storage whenever filters change
  useEffect(() => {
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    saveToStorage(userEmail, { client: clientFilter, matter: matterFilter });
  }, [userEmail, clientFilter, matterFilter]);

  const setClientFilter = useCallback((client: string | null) => {
    setClientFilterState(client);
    // Matter is scoped to client; changing or clearing client invalidates the matter selection
    setMatterFilterState(null);
  }, []);

  const setMatterFilter = useCallback((matterId: string | null, client?: string | null) => {
    setMatterFilterState(matterId);
    // When selecting a specific matter, optionally auto-fill its parent client for clarity
    if (client) setClientFilterState(client);
  }, []);

  const clearAll = useCallback(() => {
    setClientFilterState(null);
    setMatterFilterState(null);
  }, []);

  const filterByClientMatter = useCallback(
    <T extends { matter_id?: string; client?: string }>(entries: T[]): T[] => {
      if (clientFilter === null && matterFilter === null) return entries;
      return entries.filter((e) => {
        if (clientFilter !== null && e.client !== clientFilter) return false;
        if (matterFilter !== null && e.matter_id !== matterFilter) return false;
        return true;
      });
    },
    [clientFilter, matterFilter],
  );

  const isFilterActive = clientFilter !== null || matterFilter !== null;

  return {
    clientFilter,
    matterFilter,
    setClientFilter,
    setMatterFilter,
    clearAll,
    filterByClientMatter,
    isFilterActive,
  };
}
